import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { faBan, faSync, faUpload } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, filter, map, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { AppNotificationComponent } from '../../services/app-notification/app-notification.component';
import { AppNotificationModel, AppNotificationType } from '../../services/app-notification/app-notification.model';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';
import { EventService } from '../../services/event/event.service';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Component({
    selector: 'pace-core-sync-indicator',
    templateUrl: './sync-indicator.component.html',
    styleUrls: ['./sync-indicator.component.scss'],
    standalone: false,
})
export class SyncIndicatorComponent implements OnInit, OnDestroy {
    public readonly offline = ConfigConstants.SYNC_STATE.OFFLINE;
    public readonly online = ConfigConstants.SYNC_STATE.ONLINE;
    public readonly syncing = ConfigConstants.SYNC_STATE.SYNCING;
    public readonly pending = ConfigConstants.SYNC_STATE.PENDING;

    public faSync = faSync;
    public faBan = faBan;
    public faUpload = faUpload;
    private window: Window;

    public syncState$: Observable<ConfigConstants.SYNC_STATE>;
    public count$: Observable<number>;
    private destroySubject = new Subject<void>();
    private offlineModalRef?: BsModalRef;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        private readonly connectivityService: ConnectivityService,
        private readonly modalService: BsModalService,
        private readonly offlineService: OfflineService,
        private readonly eventService: EventService,
    ) {
        /**
         * When a sync process is running, the icon should be "rotating".
         * If there are any pending requests to backend then "upload" icon should be displayed.
         * In case internet is available, and authentication was successful, and SAP is reachable,
         * then the sync icon should show as "online", otherwise "offline".
         */
        this.syncState$ = combineLatest([
            this.offlineService.isSyncing$,
            this.offlineService.isRequestPending$,
            this.connectivityService.isOnline$,
        ]).pipe(
            map(([isSyncing, isPending, isOnline]) => {
                if (isSyncing) {
                    return ConfigConstants.SYNC_STATE.SYNCING;
                } else if (isPending) {
                    return ConfigConstants.SYNC_STATE.PENDING;
                }
                return isOnline ? ConfigConstants.SYNC_STATE.ONLINE : ConfigConstants.SYNC_STATE.OFFLINE;
            }),
        );
        this.count$ = this.offlineService.pendingRequestCount$;
        this.eventService.synchronizationRequested$
            .pipe(
                exhaustMap(() => {
                    this.log.debug(
                        'SyncIndicatorComponent: triggerSynchronization: sendOfflineDataAndReceiveNotificationsAndBootStock',
                    );
                    return this.synchronizationService.sendOfflineDataAndReceiveNotificationsAndBootStock();
                }),
                catchError((error: Error) => {
                    this.log.error(`SyncIndicatorComponent: triggerSynchronization: Error: ${error?.message}`);
                    return of(undefined);
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();
    }

    public ngOnInit(): void {
        if (!this.connectivityService.isInternetConnectionAvailable()) {
            this.displayOfflineAlert();
        }

        this.connectivityService.isOnline$
            .pipe(
                filter(Boolean),
                tap(() => this.hideOfflineAlert()),
                takeUntil(this.destroySubject),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    /**
     * Function to trigger a manual synchronization.
     */
    public triggerSynchronization(): void {
        this.eventService.triggerSynchronization();
    }

    private displayOfflineAlert(): void {
        const type = AppNotificationType.INFO;
        const titleText = <string>this.translate.instant('APP_START_ERROR_START_IN_OFFLINE_MODE');
        const bodyText = <string>this.translate.instant('APP_START_ERROR_NO_INTERNET');

        this.offlineModalRef = this.modalService.show(AppNotificationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new AppNotificationModel(type, titleText, bodyText),
            },
            ariaLabelledBy: 'app-notification-modal-title',
        });
    }

    private hideOfflineAlert(): void {
        this.offlineModalRef?.hide();
        this.offlineModalRef = undefined;
    }
}
